import React from 'react'
import { useDispatch } from 'react-redux'

import {
  IGoodServiceTicketData,
  ISelectableGoodServiceTicket,
  Tap2GOTicketTicketStatus,
} from 'meta/pages/goodsandservices'
import { IEventData } from 'meta/pages/v4/event'
import { ILocationData } from 'meta/pages/v4/location'
import { IUserTicketData } from 'meta/pages/v4/ticket'
import { TicketActions } from 'store/pages/ticket'
import { Dates } from 'utils/dates'

import GSTicketCard from 'components/Pages/GoodsAndServices/GSTicketCard'
import GSTicketSkeleton from 'components/Pages/GoodsAndServices/GSTicketSkeleton'
import { useUserTicketData } from 'hooks/useTicket'
import { useTypeTicketData } from 'hooks/useTypeTicket'

import { getTap2GoDataFromV4Entities } from './adapter'

type UserTicketProps = {
  cod: string
  idx: number
  activeCard: number
  setShowTap2GoModal(data: IGoodServiceTicketData): void
  setShowQRCodeModal(data: IGoodServiceTicketData): void
  setShowFullDescriptionModal(data: IGoodServiceTicketData): void
  setUnusableTicketId(idx: number): void
  handleAvailableTicketData(data: ISelectableGoodServiceTicket, remove?: boolean): void
  checkedAt?: string
  checkedTicketdIds: number[]
  availableTicketIds: number[]
  unusableTicketIds: number[]
  eventData: IEventData
  locationData: ILocationData
}

export type TypeTicketProps = {
  baseProps: UserTicketProps
  ticketData: IUserTicketData
}

const UserTicketContainer: React.FC<UserTicketProps> = (props) => {
  const { cod } = props

  const userTicketData = useUserTicketData({ cod })

  if (!userTicketData) {
    return <GSTicketSkeleton />
  }

  return <TypeTicketContainer baseProps={props} ticketData={userTicketData} />
}

const TypeTicketContainer: React.FC<TypeTicketProps> = (props) => {
  const { ticketData, baseProps } = props

  const {
    idx,
    activeCard,
    handleAvailableTicketData,
    setShowTap2GoModal,
    setShowFullDescriptionModal,
    setShowQRCodeModal,
    setUnusableTicketId,
    availableTicketIds,
    checkedTicketdIds,
    unusableTicketIds,
    checkedAt,
    eventData,
    locationData,
  } = baseProps

  const typeTicketData = useTypeTicketData({ id: ticketData.typeTicketId })

  const dispatch = useDispatch()

  if (!typeTicketData) {
    return <GSTicketSkeleton />
  }

  const gsTicketData = getTap2GoDataFromV4Entities(eventData, locationData, ticketData, typeTicketData)

  if (checkedTicketdIds.includes(idx) && checkedAt && gsTicketData.status !== Tap2GOTicketTicketStatus.checked) {
    gsTicketData.status = Tap2GOTicketTicketStatus.checked
    gsTicketData.dateEntry_tz = checkedAt

    dispatch(TicketActions.setTicketAsChecked({ code: ticketData.cod, checkedAt }))
  }

  const isEventEnded = Dates.isAfter(new Date(), new Date(gsTicketData.validTo_tz))
  const isTicketValid =
    gsTicketData.status == Tap2GOTicketTicketStatus.valid && !isEventEnded && Boolean(gsTicketData.secret)

  if (!isTicketValid && !unusableTicketIds.includes(idx)) {
    setUnusableTicketId(idx)
  }

  if (isTicketValid && !availableTicketIds.includes(idx)) {
    handleAvailableTicketData({ idx, data: gsTicketData })
  }

  if (![idx - 1, idx, idx + 1].includes(activeCard)) {
    return null
  }

  return (
    <GSTicketCard
      insideSlider
      insideWallet
      data={gsTicketData}
      showTap2GoModal={() => {
        setShowTap2GoModal(gsTicketData)
      }}
      showQRCodeModal={() => {
        setShowQRCodeModal(gsTicketData)
      }}
      showFullDescriptionModal={() => {
        setShowFullDescriptionModal(gsTicketData)
      }}
    />
  )
}

export default UserTicketContainer
